import { BASEURL, Request, TRequestMethod } from "./request.ts";
import { Schema, type SchemaObject } from "./validation.ts";
import type { BuildingTypeKeys } from "./get_trade_measure.ts";

/** GeoJSON Polygon: Ringe aus [lng, lat]-Koordinatenpaaren. */
export interface ESRGEOPolygon {
  type: "Polygon";
  coordinates: Array<Array<[number, number]>>;
}

export interface ESRGEORoofSegment {
  area_m2: number;
  tilt_deg: number;
  azimuth_deg: number;
}

export interface ESRGEORoof {
  area_m2: number;
  type_code: string;
  segments: ESRGEORoofSegment[];
}

export interface ESRGEOFacade {
  area_gross_m2: number;
  area_net_m2: number;
  area_below_grade_m2: number;
  shared_wall_share: number;
  window_area_est_m2: number;
  window_area_source: string;
}

export interface ESRGEOBasement {
  art: string;
  quelle: string;
  hinweis: string;
  indizien: string[];
  vermutet: boolean;
  modell_version: string;
  beheizt_vermutet: boolean;
  unterkellert_prior: number;
  wahrscheinlichkeit: number;
  geschaetzte_hoehe_m: number;
  beheizt_einschaetzung: string;
  sockelgeschoss_erkannt: boolean;
  vollgeschoss_schwelle_m: number;
  beheizt_wahrscheinlichkeit: number;
}

export interface ESRGEOBuildingTypeSuggestion {
  type: string;
  confidence: number;
  reasons: string[];
}

export interface ESRGEOBuildingQuality {
  solid_closed: boolean;
  warnings: string[];
}

export interface ESRGEOBuilding {
  building_id: string;
  region: string;
  municipality: string;
  zip: string;
  roof: ESRGEORoof;
  facade: ESRGEOFacade;
  footprint_m2: number;
  ridge_height_m: number;
  eaves_height_m: number;
  volume_m3: number;
  storeys: number;
  storeys_source: string;
  function_code: string;
  living_area_est_m2: number;
  basement: ESRGEOBasement;
  building_type_suggestion: ESRGEOBuildingTypeSuggestion;
  geometry_source: string;
  source_date: string;
  quality: ESRGEOBuildingQuality;
}

export interface ESRGEOParcel {
  label: string;
  gemarkung: string | null;
  flur: string;
  parcel_key: string;
  area_m2: number;
  coverage_ratio: number;
  geometry: ESRGEOPolygon;
  source: string;
  unavailable_reason: string | null;
  valid_from: string;
}
export interface ESRGEOFootPrint{
        "type": string,
        "coordinates": Array<[number, number]>
      }
export interface ESRGEOParcelBuilding{
    "building_id": string,
    "function_code": string,
    "function_label": string,
    "footprint_m2": number,
    "footprint": ESRGEOFootPrint,
    "roof_area_m2": number,
    "roof_type_code": string,
    "roof_type_label": string,
    "roof_segments": Array<ESRGEORoofSegment>,
    "eaves_height_m": number,
    "ridge_height_m": number,
    "geometry_source": string,
    "source_date": string
}
export interface ESRGEOSiteplanBasemap {
  type: string;
  url_template: string;
  url_bbox_template: string;
  attribution: string;
}

export interface ESRGEOSiteplan {
  footprint: ESRGEOPolygon;
  footprint_area_m2: number;
  basemap: ESRGEOSiteplanBasemap;
}

export interface ESRGEOModelConventions {
  north: string;
  origin: string;
  units: string;
  up: string;
}

export interface ESRGEOModel {
  format: string;
  url: string;
  size_bytes: number;
  expires_at: string;
  conventions: ESRGEOModelConventions;
}

export interface ESRGEOViewer {
  embed_url: string;
  expires_at: string;
}

export interface ESRGEOUsage {
  billable: boolean;
  quota_remaining: number;
}

export interface ESRGEOMunicipality {
  ags: string;
  name: string;
  type: string | null;
  kreis: string;
  verbund: string;
  land_code: string;
  land: string;
  population: number;
  area_km2: number;
}

export interface ESRGEOClimate {
  region_code: string;
  region_name: string;
  heating_days: number;
  theta_e_base: number;
  climate_factor: number;
  temp_mean_c: number;
  radiation_kwh_m2a: number;
}

export interface ESRGEOCensus {
  buildings_total: number;
  baujahr: Record<string, number>;
  heizungsart: Record<string, number>;
  energietraeger: Record<string, number>;
}

export interface ESRGEOZones {
  snow_zone: string;
  snow_zone_note: string;
  snow_load_kn_m2: number;
  wind_zone: string | null;
  mietenstufe: number | null;
}

export interface ESRGEOHeatPlanningArea {
  kind: string;
  name: string;
  target_year: number;
  source_url: string;
  method: string;
  confidence: string;
}

export interface ESRGEOHeatPlanningPrice {
  network: string;
  valid_from: string;
  arbeitspreis_ct_kwh_netto: number;
  leistungspreis_eur_kw_a_netto: number;
  grundpreis_eur_a_netto: number;
  messpreis_eur_a_netto: number;
  source_url: string;
}

export interface ESRGEOHeatPlanning {
  //plan_status: string;
  // plan_url: string;
  // plan_year: string;
  //fernwaerme: string;
  // fernwaerme_operator: string;
  // fernwaerme_expansion: string;
  // fernwaerme_check_url: string;
  //gis_url: string;
  confidence: string;
  area: ESRGEOHeatPlanningArea;
  prices: ESRGEOHeatPlanningPrice[];
}

export interface ESRGEODossier {
  building_id: string;
  elevation_m: number;
  municipality: ESRGEOMunicipality;
  climate: ESRGEOClimate;
  parcel: Pick<ESRGEOParcel, "label" | "valid_from">;
  heat_planning: ESRGEOHeatPlanning | null;
  census: ESRGEOCensus;
  zones: ESRGEOZones;
  attribution: string[];
}

export interface ESRGEOHouseCanditate{
    "building_id": string,
    "adz": string,
    "municipality": string,
    "footprint_m2": number
}

export interface ESRGEOMatch {
  status: 'exact' | 'unique_house' | 'ambiguous';
  candidates: Array<ESRGEOHouseCanditate> | null;
}

export interface ESRGEOCoverage {
  region: string;
  status: 'in_progress' | string;
}

export interface ESRGEOConstructionEraSuggestion{
  "era_class": "bis1918" | "bis1918" | "1919_1948" | "1949_1957" | "1958_1968" | "1969_1994" | "1995_2009" | "ab2010",
  "lower_bound_year": number, 
  "confidence": number,
  "reasons": Array<string> 
}

export interface ESRGEOConstructionYearSuggestion{
    "year": number,
    "confidence": number,
    "accept": boolean,
    "threshold": number,
    "source": string,
    "era_class": string,
    "lower_bound_year": number,
    "reasons": Array<string>,
}
export interface ESRGEOHeatingSuggestion{ 
  "energy_carrier": string, 
  "energy_carrier_share": number,
  "energy_carrier_distribution": Record<string, number>,
  "heizungsart":string,
  "heizungsart_distribution": {"Etagenheizung": number, "Zentralheizung": number},
  "district_heating_signal": boolean,
  "confidence": number,
  "reasons": Array<string>
}

export interface ESRBuildingGeometryResponse {
  sim_id: string;
  created_at: string;
  match: ESRGEOMatch;
  coverage: ESRGEOCoverage;
  building: ESRGEOBuilding;
  parcel: ESRGEOParcel;
  parcel_buildings: Array<ESRGEOParcelBuilding>;
  siteplan: ESRGEOSiteplan;
  model: ESRGEOModel;
  viewer: ESRGEOViewer;
  usage: ESRGEOUsage;
  dossier: ESRGEODossier;
  construction_era_suggestion : ESRGEOConstructionEraSuggestion ;
  construction_year_suggestion: ESRGEOConstructionYearSuggestion;
  heating_suggestion : ESRGEOHeatingSuggestion;
  // Flattened, enriched fields returned by the endpoint and consumed by the
  // client (see ESR-core api_building_geometry). Optional because they are not
  // present in every response variant.
  match_quality?: string;
  roof_segments?: Array<ESRGEORoofSegment>;
  reasons?: Array<string>;
  building_id?: string;
  roof_area_m2?: number;
  storeys?: number;
  living_area_est_m2?: number;
  building_type_accept?: boolean;
  building_type_suggestion?: { type: BuildingTypeKeys };
  candidates?: Array<ESRGEOHouseCanditate>;
  glb_url?: string;
}

export interface ESRGEO3dAdress{
    zip: string;
    street: string;
    num: string;
}

// Hinweis: SchemaObject unterstützt nur eine Array-Ebene von Primitiven,
// daher werden die verschachtelten GeoJSON-Koordinaten hier nur flach als
// number[] validiert (Tiefen-Validierung der Ringe ist nicht abbildbar).
const ESRGEOPolygonSchema : SchemaObject = {
    "type": 'string',
    "coordinates": ['number'],
}

const ESRGEORoofSegmentSchema : SchemaObject = {
    "area_m2": 'number',
    "tilt_deg": 'number',
    "azimuth_deg": 'number',
}

const ESRGEORoofSchema : SchemaObject = {
    "area_m2": 'number',
    "type_code": 'string',
    "segments": [ESRGEORoofSegmentSchema],
}

const ESRGEOFacadeSchema : SchemaObject = {
    "area_gross_m2": 'number',
    "area_net_m2": 'number',
    "area_below_grade_m2": 'number',
    "shared_wall_share": 'number',
    "window_area_est_m2": 'number',
    "window_area_source": 'string',
}

const ESRGEOBasementSchema : SchemaObject = {
    "art": 'string',
    "quelle": 'string',
    "hinweis": 'string',
    "indizien": ['string'],
    "vermutet": 'boolean',
    "modell_version": 'string',
    "beheizt_vermutet": 'boolean',
    "unterkellert_prior": 'number',
    "wahrscheinlichkeit": 'number',
    "geschaetzte_hoehe_m": 'number',
    "beheizt_einschaetzung": 'string',
    "sockelgeschoss_erkannt": 'boolean',
    "vollgeschoss_schwelle_m": 'number',
    "beheizt_wahrscheinlichkeit": 'number',
}

const ESRGEOBuildingTypeSuggestionSchema : SchemaObject = {
    "type": 'string',
    "confidence": 'number',
    "reasons": ['string'],
}

const ESRGEOBuildingQualitySchema : SchemaObject = {
    "solid_closed": 'boolean',
    "warnings": ['string'],
}

const ESRGEOBuildingSchema : SchemaObject = {
    "building_id": 'string',
    "region": 'string',
    "municipality": 'string',
    "zip": 'string',
    "roof": ESRGEORoofSchema,
    "facade": ESRGEOFacadeSchema,
    "footprint_m2": 'number',
    "ridge_height_m": 'number',
    "eaves_height_m": 'number',
    "volume_m3": 'number',
    "storeys": 'number',
    "storeys_source": 'string',
    "function_code": 'string',
    "living_area_est_m2": 'number',
    "basement": ESRGEOBasementSchema,
    "building_type_suggestion": ESRGEOBuildingTypeSuggestionSchema,
    "geometry_source": 'string',
    "source_date": 'string',
    "quality": ESRGEOBuildingQualitySchema,
}

const ESRGEOParcelSchema : SchemaObject = {
    "label": 'string',
    "gemarkung": 'string',
    "flur": 'string',
    "parcel_key": 'string',
    "area_m2": 'number',
    "coverage_ratio": 'number',
    "geometry": ESRGEOPolygonSchema,
    "source": 'string',
    "unavailable_reason": 'string',
    "valid_from": 'string',
}

const ESRGEOSiteplanBasemapSchema : SchemaObject = {
    "type": 'string',
    "url_template": 'string',
    "attribution": 'string',
}

const ESRGEOSiteplanSchema : SchemaObject = {
    "footprint": ESRGEOPolygonSchema,
    "footprint_area_m2": 'number',
    "basemap": ESRGEOSiteplanBasemapSchema,
}

const ESRGEOModelConventionsSchema : SchemaObject = {
    "north": 'string',
    "origin": 'string',
    "units": 'string',
    "up": 'string',
}

const ESRGEOModelSchema : SchemaObject = {
    "format": 'string',
    "url": 'string',
    "size_bytes": 'number',
    "expires_at": 'string',
    "conventions": ESRGEOModelConventionsSchema,
}

const ESRGEOViewerSchema : SchemaObject = {
    "embed_url": 'string',
    "expires_at": 'string',
}

const ESRGEOUsageSchema : SchemaObject = {
    "billable": 'boolean',
    "quota_remaining": 'number',
}

const ESRGEOMunicipalitySchema : SchemaObject = {
    "ags": 'string',
    "name": 'string',
    "type": 'string',
    "kreis": 'string',
    "verbund": 'string',
    "land_code": 'string',
    "land": 'string',
    "population": 'number',
    "area_km2": 'number',
}

const ESRGEOClimateSchema : SchemaObject = {
    "region_code": 'string',
    "region_name": 'string',
    "heating_days": 'number',
    "theta_e_base": 'number',
    "climate_factor": 'number',
    "temp_mean_c": 'number',
    "radiation_kwh_m2a": 'number',
}

const ESRGEOCensusSchema : SchemaObject = {
    "buildings_total": 'number',
    "baujahr": {},
    "heizungsart": {},
    "energietraeger": {},
}

const ESRGEOZonesSchema : SchemaObject = {
    "snow_zone": 'string',
    "snow_zone_note": 'string',
    "snow_load_kn_m2": 'number',
    "wind_zone": 'string',
    "mietenstufe": 'number',
}

const ESRGEOHeatPlanningAreaSchema : SchemaObject = {
    "kind": 'string',
    "name": 'string',
    "target_year": 'number',
    "source_url": 'string',
    "method": 'string',
    "confidence": 'string',
}

const ESRGEOHeatPlanningPriceSchema : SchemaObject = {
    "network": 'string',
    "valid_from": 'string',
    "arbeitspreis_ct_kwh_netto": 'number',
    "leistungspreis_eur_kw_a_netto": 'number',
    "grundpreis_eur_a_netto": 'number',
    "messpreis_eur_a_netto": 'number',
    "source_url": 'string',
}

const ESRGEOHeatPlanningSchema : SchemaObject = {
    //"plan_status": 'string',
    // "plan_url": 'string',
    // "plan_year": 'string',
    //"fernwaerme": 'string',
    // "fernwaerme_operator": 'string',
    // "fernwaerme_expansion": 'string',
    // "fernwaerme_check_url": 'string',
    //"gis_url": 'string',
    "confidence": 'string',
    "area": ESRGEOHeatPlanningAreaSchema,
    "prices": [ESRGEOHeatPlanningPriceSchema],
}

const ESRGEODossierSchema : SchemaObject = {
    "building_id": 'string',
    "elevation_m": 'number',
    "municipality": ESRGEOMunicipalitySchema,
    "climate": ESRGEOClimateSchema,
    "parcel": {
        "label": 'string',
        "valid_from": 'string',
    },
    //"heat_planning": ESRGEOHeatPlanningSchema,
    "census": ESRGEOCensusSchema,
    "zones": ESRGEOZonesSchema,
    "attribution": ['string'],
}

const ESRGEOHouseCanditateSchema : SchemaObject = {
    "building_id": 'string',
    "adz": 'string',
    "municipality": 'string',
    "footprint_m2": 'number',
}

const ESRGEOMatchSchema : SchemaObject = {
    "status": 'string',
    "candidates": [ESRGEOHouseCanditateSchema],
}

const ESRGEOCoverageSchema : SchemaObject = {
    "region": 'string',
    "status": 'string',
}

const ESRBuildingGeometryResponseSchema : SchemaObject = {
    "sim_id": 'string',
    "created_at": 'string',
    "match": ESRGEOMatchSchema,
    "coverage": ESRGEOCoverageSchema,
    "building": ESRGEOBuildingSchema,
    "parcel": ESRGEOParcelSchema,
    "siteplan": ESRGEOSiteplanSchema,
    "model": ESRGEOModelSchema,
    "viewer": ESRGEOViewerSchema,
    "usage": ESRGEOUsageSchema,
    "dossier": ESRGEODossierSchema,
}

export type ESRGEOInclude = "context_buildings" | "parcels";
export class ESRBuildingGeometry extends Request<ESRBuildingGeometryResponse>{
    override method : TRequestMethod = "POST";
    override responseSchema : Schema = ESRBuildingGeometryResponseSchema;

    constructor (
        public sessionToken: string,
        public zip: string,
        public street: string,
        public num: string,
        public include: Array<ESRGEOInclude> = ["context_buildings","parcels"]
    ){
        super();
    }

    override route() : string{
        return `${BASEURL}/building_geometry?sessionToken=${this.sessionToken}`;
    }
}


// curl -X POST https://geo3d.services/v1/simulations \
//   -H 'X-Api-Key: g3d_live_wdZECS965dPFxpI6NZ0HH2CR' \
//   -H 'X-Tenant: febis-dev' \
//   -H 'Content-Type: application/json' \
//   -d '{"address":{"zip":"61389","street":"Gartenstraße","housenumber":"3"}}'


